import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import { catchError, from, map, Observable, retry, take, tap, throwError, timeout, timer } from 'rxjs';
import { environment } from 'src/environments/environment';

export type HealthFailureReason = 'offline' | 'unreachable' | null;

@Injectable({
    providedIn: 'root',
})
export class HealthService {
    private http: HttpClient = inject(HttpClient);

    private _healthy: WritableSignal<boolean | null> = signal(null);
    private _failureReason: WritableSignal<HealthFailureReason | null> = signal(null);
    public healthy: Signal<boolean | null> = this._healthy.asReadonly();
    public failureReason: Signal<HealthFailureReason | null> = this._failureReason.asReadonly();

    private readonly REQUEST_TIMEOUT_MS: number = 8_000;
    private readonly RETRY_COUNT: number = 2;
    private readonly RETRY_DELAY_MS: number = 1_500;

    public checkApiServerStatus(): Observable<boolean> {
        return this.http.get(`${environment.apiUrl}/system/status`, { responseType: 'text' }).pipe(
            timeout(this.REQUEST_TIMEOUT_MS),
            retry({
                count: this.RETRY_COUNT,
                delay: (_error, retryIndex) => timer(retryIndex * this.RETRY_DELAY_MS),
                resetOnSuccess: true,
            }),
            take(1),
            map(() => true),
            catchError((error) => {
                if (environment.debug) {
                    console.warn('Health check failed', error);
                }

                return throwError(() => error);
            }),
            tap((ok) => {
                this._healthy.set(ok);
                this._failureReason.set(null);
            }),
            catchError(() =>
                from(Network.getStatus()).pipe(
                    map((status: ConnectionStatus) => {
                        this._healthy.set(false);
                        this._failureReason.set(status.connected ? 'unreachable' : 'offline');
                        return false;
                    })
                )
            )
        );
    }
}
